import React, { useState } from 'react';
import { BiPlus } from 'react-icons/bi';
import { IoTrash } from 'react-icons/io5';
import { LuDownload } from 'react-icons/lu';
import { useWindowSize } from 'usehooks-ts';
import styles from './etaiement-attachments.module.scss';
import { useAddEtaiementFile, useDeleteEtaiementFile } from '~/hooks/etaiements';
import ErrorField from '~/lib/HooksFormFields/ErrorField';
import withModalConfirm from '~/lib/withModalConfirm';
import type { IEtaiementFile } from '~/types/etaiements';
import { downloadFile } from '~/utils';

type ConfirmFn = (
  message: string,
  action?: () => void,
  cancel?: () => void,
) => void;

const ATTACHMENT_ACCEPT = '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png';
const ATTACHMENT_MIMES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
const ATTACHMENT_EXTS = ['.pdf', '.jpg', '.jpeg', '.png'];
const ATTACHMENT_TYPE_ERROR = 'Le fichier doit être un PDF, JPG, JPEG ou PNG.';

const getFileExtension = (fileName: string): string => {
  const lastDot = fileName.lastIndexOf('.');
  if (lastDot < 0) return '';
  return fileName.slice(lastDot).toLowerCase();
};

const isAllowedAttachment = (file: File): boolean => {
  const extension = getFileExtension(file.name);
  if (!ATTACHMENT_EXTS.includes(extension)) return false;
  if (!file.type) return true;
  return ATTACHMENT_MIMES.includes(file.type);
};

const formatFileDate = (date: Date): string => (
  new Intl.DateTimeFormat('fr', {
    year: 'numeric',
    month: 'long',
    day: '2-digit',
  }).format(date)
);

const formatFileSize = (sizeInKo: number): string => {
  if (sizeInKo >= 1000000) {
    const sizeInMo = sizeInKo / 1000000;
    return `${sizeInMo.toFixed(2)} MO`;
  }
  const sizeInKoRound = sizeInKo / 1000;
  return `${sizeInKoRound.toFixed(0)} KO`;
};

const FileRow = ({
  file,
  etaiementId,
  isPreview,
  confirm,
}: {
  file: IEtaiementFile,
  etaiementId: string,
  isPreview: boolean,
  confirm: ConfirmFn,
}) => {
  const { mutate: deleteFile } = useDeleteEtaiementFile();
  const token = localStorage.getItem('token')?.replace('JWT ', '');
  const { width } = useWindowSize();
  const fileUrl = `${process.env.REACT_APP_API_URL}/files/public/${file.file.path}?token=${token}`;
  const isImage = Boolean(file.file.mimetype?.startsWith('image/'));

  const handleDelete = () => {
    confirm(
      'Êtes-vous sûr de vouloir supprimer ce fichier ?',
      () => {
        deleteFile({
          fileId: file.file?._id,
          etaiementId,
        });
      },
      () => null,
    );
  };

  if (width <= 1024) {
    return (
      <div className={styles.card}>
        <div className={styles.infos}>
          {isImage && (
            <div className={styles.picture}>
              <img src={fileUrl} alt='' />
            </div>
          )}
          <div className={styles.title}>
            <h3>{file?.file?.name}</h3>
            <p>{file.createdAt && formatFileDate(new Date(file.createdAt))}</p>
          </div>
        </div>
        <div className={styles.actions}>
          <a
            onClick={() => downloadFile(fileUrl, file.file.name)}
            download
          >
            {LuDownload({})}
          </a>
          {!isPreview && (
            <button
              type="button"
              onClick={handleDelete}
            >
              {IoTrash({})}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.row}>
      <div className={styles.col}>
        {isImage && (
          <div className={styles.picture}>
            <img src={fileUrl} alt='' />
          </div>
        )}
        <p>{file?.file?.name}</p>
      </div>
      <div className={styles.col}>
        <p>{file.author?.profile?.firstName} {file.author?.profile?.lastName}</p>
      </div>
      <div className={styles.col}>
        <p>{file.createdAt && formatFileDate(new Date(file.createdAt))}</p>
      </div>
      <div className={styles.col}>
        <p>{file?.size ? formatFileSize(file.size) : '---'}</p>
      </div>
      <div className={styles.col}>
        <a
          onClick={() => downloadFile(fileUrl, file.file.name)}
          download
        >
          {LuDownload({})}
        </a>
        {!isPreview && (
          <button
            type="button"
            onClick={handleDelete}
          >
            {IoTrash({})}
          </button>
        )}
      </div>
    </div>
  );
};

const EtaiementAttachments = ({
  etaiementId,
  files,
  isPreview,
  confirm,
}: {
  etaiementId: string,
  files?: IEtaiementFile[],
  isPreview: boolean,
  confirm: ConfirmFn,
}) => {
  const { mutateAsync: addEtaiementFile } = useAddEtaiementFile('Fichier ajouté');
  const [fileError, setFileError] = useState<string | null>(null);
  const attachments = files || [];

  const handleAddFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const file = input.files?.[0];
    input.value = '';
    if (!file || !etaiementId) return;
    if (!isAllowedAttachment(file)) {
      setFileError(ATTACHMENT_TYPE_ERROR);
      return;
    }
    setFileError(null);
    await addEtaiementFile({
      _id: etaiementId,
      file,
    });
  };

  return (
    <div className={styles.attachments}>
      <div className={styles.header}>
        <div>
          <h4>Pièces jointes</h4>
          <p className={styles.hint}>PDF, JPG, JPEG ou PNG</p>
        </div>
        {!isPreview && (
          <div className={styles.add}>
            <label htmlFor="etaiement-attachment-input">
              {BiPlus({})}
              Ajouter un fichier
            </label>
            <input
              id="etaiement-attachment-input"
              type="file"
              accept={ATTACHMENT_ACCEPT}
              onChange={handleAddFile}
            />
          </div>
        )}
      </div>
      {fileError && <ErrorField message={fileError} />}
      {attachments.length === 0
        ? <p className={styles.empty}>Aucun fichier</p>
        : (
          <div className={styles.files}>
            {attachments.map((file) => (
              <FileRow
                key={file._id}
                file={file}
                etaiementId={etaiementId}
                isPreview={isPreview}
                confirm={confirm}
              />
            ))}
          </div>
        )
      }
    </div>
  );
};

export default withModalConfirm(EtaiementAttachments);
